import {
  BUNDLE_OOS_MESSAGE,
  EXPERIENCE_TYPE_GM,
  UNAVAILABLE_EXPERIENCES,
  ASSORTMENT_STRATEGY,
  PRODUCTS_CONFIGURATION,
  BUNDLE_TYPE_PACKAGE,
  EXPERIENCE_TYPE_LAUNDRY
} from '../constants/index';
import { compareString } from './selectable-variant-utils';

export const getExperienceType = (product) => {
  return product?.bundleSpecificationDetails?.experienceType || EXPERIENCE_TYPE_GM;
};

export const getBundleType = (bundle) => bundle?.bundleSpecificationDetails?.type;

export const isProductDiscontinued = ({ product, checkAvailabilityData = null }) => {
  const isDiscontinuedProduct = checkAvailabilityData?.checkAvailability?.fulfillmentOptions?.some(
    (option) => option?.services?.some(
      (service) => service?.deliveryAvailabilityDetail?.some(
        (detail) => {
          return detail?.assortmentStrategy === ASSORTMENT_STRATEGY && detail?.itemId === product?.itemId;
        }
      )
    )
  );
  return isDiscontinuedProduct || !!product?.availabilityType?.discontinued;
};

export const isOOS = ({ product }) => product?.isOOS;

export const isDiscontinuedItem = ({ product }) => product?.isDiscontinued;

export const isHiddenItem = ({ product }) => compareString(product?.identifiers?.hideItem, 'true');

export const isObsoleteItem = ({ product }) => product?.availabilityType?.obsolete;

const hasIncompleteItems = ({ products }) => products?.some((product) => product?.isIncomplete);

export const bundleHasOOSProduct = ({ products }) =>
  products?.some((product) => isOOS({ product }));

export const bundleHasDiscontinuedProduct = ({ products }) =>
  products?.some((product) => product.isDiscontinued);

export const bundleHasHiddenProduct = ({ products }) =>
  products?.some((product) => isHiddenItem({ product }));

export const bundleHasObsoleteProduct = ({ products }) =>
  products?.some((product) => isObsoleteItem({ product })) || hasIncompleteItems({ products });

export const isProductUnavailable = ({ product }) => {
  if (!product) return false;
  return isOOS({ product })
    || isDiscontinuedItem({ product })
    || isHiddenItem({ product })
    || isObsoleteItem({ product });
};

/**
 * Determines the unavailability scenarios for a given product.
 * Checks multiple conditions (out of stock, discontinued, hidden, obsolete) and returns
 * an object indicating which scenarios apply to the product.
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.product - The product object to check for unavailability scenarios
 * @returns {Object} An object containing boolean flags for each unavailability scenario:
 *   - {boolean} [oos] - True if the product is out of stock
 *   - {boolean} [discontinued] - True if the product is discontinued
 *   - {boolean} [hidden] - True if the product is hidden
 *   - {boolean} [obsolete] - True if the product is obsolete
 */
const getUnavailabilityScenarios = ({ product }) => {
  const unavailabilityScenarios = {};

  if (isOOS({ product })) {
    unavailabilityScenarios.oos = true;
  }
  if (isDiscontinuedItem({ product })) {
    unavailabilityScenarios.discontinued = true;
  }
  if (isHiddenItem({ product })) {
    unavailabilityScenarios.hidden = true;
  }
  if (isObsoleteItem({ product })) {
    unavailabilityScenarios.obsolete = true;
  }

  return unavailabilityScenarios;
};

export const isBundleUnavailable = ({ products }) =>
  products?.some((product) => isProductUnavailable({ product }))
  || hasIncompleteItems({ products });

export const getUnavailableMessage = ({ product, bundleProduct }) => {
  if (!product) return null;

  if (isOOS({ product })) {
    return BUNDLE_OOS_MESSAGE;
  }

  const isUnavailable = isProductUnavailable({ product });
  if (isUnavailable) {
    return bundleProduct?.features?.unavailableAlertMessageText;
  }

  return null;
};

/**
 * Determines the appropriate alert message configuration based on product availability status.
 *
 * Checks a list of products for unavailability scenarios and returns the corresponding alert
 * configuration. OOS scenario is distinguished from other unavailability types if it is the only scenario present.
 *
 * @param {Array<Object>} productList - Array of product objects to check for availability
 * @returns {Object|null} Alert configuration object containing accessoryAlertMessage,
 *                        accessoryAlertHeadline, and accessoryAlertStatus properties,
 *                        or null if all products are available
 *
 * @example
 * const products = [{ status: 'discontinued' }, { status: 'available' }];
 * const alert = getUnavailableAlertMessage(products);
 * // Returns: { accessoryAlertMessage: '...', accessoryAlertHeadline: '...', accessoryAlertStatus: '...' }
 */
export const getUnavailableAlertMessage = (productList) => {
  const {
    unavailableAlertAdditionalText: accessoryAlertMessage,
    unavailableAlertHeadline,
    oosAlertHeadline,
    unavailableAlertStatus: accessoryAlertStatus
  } = PRODUCTS_CONFIGURATION[BUNDLE_TYPE_PACKAGE][EXPERIENCE_TYPE_LAUNDRY];

  const hasNonOOSUnavailability = productList.some((product) => {
    const { discontinued, hidden, obsolete } = getUnavailabilityScenarios({ product });
    return discontinued || hidden || obsolete;
  });

  if (hasNonOOSUnavailability) {
    return {
      accessoryAlertMessage,
      accessoryAlertHeadline: unavailableAlertHeadline,
      accessoryAlertStatus
    };
  }

  const hasOOS = productList.some((product) => {
    const { oos } = getUnavailabilityScenarios({ product });
    return oos;
  });

  if (hasOOS) {
    return {
      accessoryAlertMessage,
      accessoryAlertHeadline: oosAlertHeadline,
      accessoryAlertStatus
    };
  }

  return null;
};

const isUnavailableExperienceType = (product) => {
  const experienceType = getExperienceType(product);
  const bundleType = getBundleType(product);
  return UNAVAILABLE_EXPERIENCES.some(
    (exp) => exp.experienceType === experienceType && exp.bundleType === bundleType
  );
};

export const getFilterResponseFlag = (productData) => !isUnavailableExperienceType(productData);

export const showEmptyContainer = (bundleProduct, product) => {
  if (!bundleProduct?.features?.emptyContainerForUnavailableProduct) {
    return false;
  }
  return product?.removed || product?.isDiscontinued || isHiddenItem({ product }) || isObsoleteItem({ product });
};

export const getOOSProductIds = ({ products }) => {
  return products?.filter((product) => isOOS({ product }))
    ?.map((product) => product.itemId) || [];
};

export const isVariantUnavailable = (itemId, selectableVariantProducts, product) => {
  const filteredProduct = product?.itemId === itemId ? product
    : selectableVariantProducts?.find((selectableVariant) => selectableVariant?.itemId === itemId);
  return isProductUnavailable({ product: filteredProduct }) || false;
};

export const getMissingProductsCount = (originalCount, products) => {
  return originalCount - (products?.length || 0);
};

/**
 * Detects if a product violates quantity limits (minimum or maximum)
 * @param {Object} product - The product to check
 * @returns {Object|null} - Error details if violation exists, null otherwise
 */
const detectQuantityViolation = ({ product }) => {
  const { itemId } = product;
  const bundleQuantity = product?.quantity || 1;

  const minimumOrderQuantity = product?.info?.minimumOrderQuantity;
  if (minimumOrderQuantity && minimumOrderQuantity > 0 && bundleQuantity < minimumOrderQuantity) {
    return {
      errorSchema: 'product.info.minimumOrderQuantity',
      errorDescription: `Product ${itemId} has quantity ${bundleQuantity} in bundle `
        + `but minimum order quantity is ${minimumOrderQuantity}`
    };
  }

  const quantityLimit = product?.info?.quantityLimit;
  if (quantityLimit && bundleQuantity > quantityLimit) {
    return {
      errorSchema: 'product.info.quantityLimit',
      errorDescription: `Product ${itemId} has quantity ${bundleQuantity} in bundle `
        + `but maximum order quantity is ${quantityLimit}`
    };
  }

  return null;
};

/**
 * Detects all data issues in a single product
 * Excludes out-of-stock items per requirements
 * Returns array of all issues found
 * @param {Object} product - The product to check
 * @returns {Array} - Array of all error objects found, empty array if no issues
 */
const detectProductDataIssues = ({ product }) => {
  if (!product || isOOS({ product })) {
    return [];
  }

  const issues = [];
  const { itemId } = product;

  if (isDiscontinuedItem({ product })) {
    issues.push({
      errorSchema: 'product.availabilityType.discontinued',
      errorDescription: `Product ${itemId} is discontinued and should not be in bundle`
    });
  }

  if (isHiddenItem({ product })) {
    issues.push({
      errorSchema: 'product.identifiers.hideItem',
      errorDescription: `Product ${itemId} is marked as hidden (hideItem=true) and should not be visible in bundle`
    });
  }

  if (isObsoleteItem({ product })) {
    issues.push({
      errorSchema: 'product.availabilityType.obsolete',
      errorDescription: `Product ${itemId} is obsolete and should not be in bundle`
    });
  }

  const quantityViolation = detectQuantityViolation({ product });
  if (quantityViolation) {
    issues.push(quantityViolation);
  }

  return issues;
};

/**
 * Detects and reports all data issues across all products in a bundle
 * Tracks checked product itemIds to prevent redundant validation
 * @param {Array} products - Array of products to check
 * @param {Set} checkedItemIdsRef - Set tracking itemIds that have already been checked
 */
export const detectAndReportBundleDataIssues = ({ products, checkedItemIdsRef }) => {
  products
    .filter((product) => product?.itemId && !checkedItemIdsRef.has(product.itemId))
    .forEach((product) => {
      const issues = detectProductDataIssues({ product });

      issues.forEach((issue) => {
        if (window.LIFE_CYCLE_EVENT_BUS) {
          window.LIFE_CYCLE_EVENT_BUS.trigger('invalid-data-scenario.found', {
            componentName: '@thd-olt-component-react/product-bundle',
            erroredSchema: issue.errorSchema,
            errorDescription: issue.errorDescription
          });
        }
      });

      checkedItemIdsRef.add(product.itemId);
    });
};